import { Injectable, Component } from '@angular/core';
import { DialogComponent } from 'src/app/pages/shared/dialog/dialog.component';
import { Observable, of } from 'rxjs';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { HeaderType } from 'src/app/dataModels/header-type';
import { AuthService } from '../authManager/auth-manager.service';

/**
 * Dialog Service to display
 * 1. Success message
 * 2. Warning message
 * 3. Info message
 * 4. error message
 * 5. Open a component
 * 6. Open a confirm window dialog
 */
@Injectable({
  providedIn: 'root',
})
export class DialogService {
  /**
   * instance of bgxBootstrapModalRef
   */
  modalDialogRef: BsModalRef;

  /**
   * list of all modal refs
   */
  modalRefs: BsModalRef[] = [];

  /**
   * Constructor to inject dependency
   * @param modalDialogService instance of ngxBsModalService
   * @param authService instance of AuthService
   */
  constructor(private modalDialogService: BsModalService, private authService: AuthService) {
    this.authService.isLoggedIn.subscribe((value) => {
      if (!value && this.modalDialogRef) {
        const modalsCount = this.modalDialogService.getModalsCount();
        for (let i = 0; i < this.modalRefs.length; i++) {
          this.modalRefs[i].hide();
        }
      }
    });
  }

  /**
   * Show dialog message
   * @param param0 pass title, message, btnOptions and type of header
   */
  showMessage({ title, message, btnOptions, type }: { title: string; message: string; btnOptions: any[]; type: HeaderType }) {
    const initialState = {
      title,
      message,
      btnOptions,
      value: '',
      type,
    };

    const config = {
      backdrop: true,
      ignoreBackdropClick: true,
    };

    this.modalDialogRef = this.modalDialogService.show(DialogComponent, {
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: false,
      class: 'modal-dialog-centered modal-lg',
      initialState,
    });

    this.modalRefs.push(this.modalDialogRef);

    return new Observable<string>(this.getConfirmSubscriber());
  }

  /**
   * Ask user to confirm an action. `message` explains the action and choices.
   * Returns observable resolving to `true`=confirm or `false`=cancel
   */
  confirm(message?: string): Observable<boolean> {
    const confirmation = window.confirm(message || 'Is it OK?');

    return of(confirmation);
  }

  /**
   * Open component as a dialog
   */
  open(component: any, data: any) {
    const initialState = {
      data,
    };

    const config = {
      backdrop: true,
      ignoreBackdropClick: true,
    };

    this.modalDialogRef = this.modalDialogService.show(component, {
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: false,
      class: 'modal-dialog-centered modal-fullscreen ',
      initialState,
    });

    this.modalRefs.push(this.modalDialogRef);

    return new Observable<any>(this.getConfirmSubscriber());
  }

  openPopUp(component: any, data: any) {
    const initialState = {
      data,
    };

    const config = {
      backdrop: true,
      ignoreBackdropClick: true,
    };

    this.modalDialogRef = this.modalDialogService.show(component, {
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: false,
      class: 'modal-dialog-centered modal-lg',
      initialState,
    });

    this.modalRefs.push(this.modalDialogRef);

    return new Observable<any>(this.getConfirmSubscriber());
  }

  openFullPopUp(component: any, data: any) {
    const initialState = {
      data,
    };

    const config = {
      backdrop: true,
      ignoreBackdropClick: true,
    };

    this.modalDialogRef = this.modalDialogService.show(component, {
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: false,
      class: 'modal-dialog-centered modal-xl',
      initialState,
    });

    this.modalRefs.push(this.modalDialogRef);

    return new Observable<any>(this.getConfirmSubscriber());
  }

  /**
   * return the value from modal dialog as subscriber
   */
  private getConfirmSubscriber() {
    return (observer) => {
      const subscription = this.modalDialogRef.content.onHide.subscribe((value) => {
        observer.next(value);
        observer.complete();
      });

      return {
        unsubscribe() {
          subscription.unsubscribe();
        },
      };
    };
  }
}
